import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { AccountPanel } from "./components/AccountPanel";
import { AdminPanel } from "./components/AdminPanel";
import { AggregateBoard } from "./components/AggregateBoard";
import { AuthModal } from "./components/AuthModal";
import { Header } from "./components/Header";
import { HotlistCard } from "./components/HotlistCard";
import { LibraryPanel } from "./components/LibraryPanel";
import { Sidebar } from "./components/Sidebar";
import { platforms as staticPlatforms } from "./data/platforms";
import { api } from "./lib/api";
import { buildAggregate } from "./lib/aggregate";
import { loadGuestState, saveGuestState } from "./lib/storage";
import type {
  AggregateItem,
  CategoryId,
  FavoriteItem,
  GuestState,
  HistoryItem,
  HotItem,
  Hotlist,
  SessionData,
} from "./types";

export function App() {
  const [hotlists, setHotlists] = useState<Hotlist[]>([]);
  const [guestState, setGuestState] = useState<GuestState>(loadGuestState);
  const [session, setSession] = useState<SessionData | null>(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query.trim().toLocaleLowerCase("zh-CN"));
  const [category, setCategory] = useState<"all" | CategoryId>("all");
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryTab, setLibraryTab] = useState<"favorites" | "history">("favorites");
  const [authOpen, setAuthOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [cloudNotice, setCloudNotice] = useState("");

  const loadPublicData = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    setLoadError("");
    try {
      setHotlists(await api.getHotlists());
    } catch {
      setLoadError("榜单接口暂时无法连接，请稍后刷新重试。");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    const nextSession = await api.getSession();
    setSession(nextSession);
    if (nextSession) {
      const merged = await api.syncGuestState(guestState);
      setGuestState(merged);
      setCloudNotice("收藏、历史和平台设置已与云端合并。");
    }
  }, [guestState]);

  useEffect(() => {
    void loadPublicData();
  }, [loadPublicData]);

  useEffect(() => {
    let active = true;
    api.getSession()
      .then(async (nextSession) => {
        if (!active) return;
        setSession(nextSession);
        if (nextSession) {
          const merged = await api.syncGuestState(loadGuestState());
          if (active) setGuestState(merged);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setAuthChecking(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    saveGuestState(guestState);
  }, [guestState]);

  useEffect(() => {
    const refreshWhenOnline = () => void loadPublicData();
    window.addEventListener("online", refreshWhenOnline);
    return () => window.removeEventListener("online", refreshWhenOnline);
  }, [loadPublicData]);

  const hotlistMap = useMemo(() => new Map(hotlists.map((hotlist) => [hotlist.platformId, hotlist])), [hotlists]);
  const aggregate = useMemo(() => buildAggregate(hotlists), [hotlists]);
  const favoriteKeys = useMemo(() => new Set(guestState.favorites.map((item) => item.key)), [guestState.favorites]);

  const visiblePlatforms = useMemo(() => {
    const platformById = new Map(staticPlatforms.map((platform) => [platform.id, platform]));
    const ordered = guestState.platformOrder.map((id) => platformById.get(id)).filter((item) => item !== undefined);
    const selected = selectedPlatform ? platformById.get(selectedPlatform) : null;
    const base = selected
      ? [selected]
      : category === "all"
        ? ordered.filter((platform) => guestState.subscriptions.includes(platform.id))
        : ordered;

    return base.filter((platform) => {
      if (category !== "all" && platform.category !== category) return false;
      if (!deferredQuery) return true;
      if (platform.name.toLocaleLowerCase("zh-CN").includes(deferredQuery)) return true;
      return (hotlistMap.get(platform.id)?.items || []).some((item) => item.title.toLocaleLowerCase("zh-CN").includes(deferredQuery));
    });
  }, [category, deferredQuery, guestState.platformOrder, guestState.subscriptions, hotlistMap, selectedPlatform]);

  const filteredHotlistMap = useMemo(() => {
    if (!deferredQuery) return hotlistMap;
    const filtered = new Map<string, Hotlist>();
    for (const [platformId, hotlist] of hotlistMap) {
      filtered.set(platformId, {
        ...hotlist,
        items: hotlist.items.filter((item) => item.title.toLocaleLowerCase("zh-CN").includes(deferredQuery)),
      });
    }
    return filtered;
  }, [deferredQuery, hotlistMap]);

  const updatedAt = useMemo(() => {
    let latest = 0;
    for (const hotlist of hotlists) {
      const timestamp = hotlist.updatedAt ? Date.parse(hotlist.updatedAt) : 0;
      if (timestamp > latest) latest = timestamp;
    }
    return latest ? new Date(latest).toISOString() : null;
  }, [hotlists]);

  function updateGuestState(nextState: GuestState) {
    setGuestState(nextState);
    if (session) {
      void api.saveSubscriptions(nextState).catch(() => setCloudNotice("平台设置已保存在浏览器，云端同步稍后重试。"));
    }
  }

  function openLibrary(tab: "favorites" | "history") {
    setLibraryTab(tab);
    setLibraryOpen(true);
  }

  function recordOpen(item: HotItem) {
    const historyItem: HistoryItem = {
      key: item.fingerprint,
      title: item.title,
      url: item.url,
      platformId: item.platformId,
      viewedAt: new Date().toISOString(),
    };
    setGuestState((current) => ({
      ...current,
      history: [historyItem, ...current.history.filter((entry) => entry.key !== historyItem.key)].slice(0, 500),
    }));
    if (session) void api.saveHistory(historyItem).catch(() => undefined);
  }

  function openAggregate(item: AggregateItem) {
    const source = item.sources[0];
    if (!source) return;
    const historyItem: HistoryItem = {
      key: item.id,
      title: item.title,
      url: source.url || item.url,
      platformId: source.platformId,
      viewedAt: new Date().toISOString(),
    };
    setGuestState((current) => ({
      ...current,
      history: [historyItem, ...current.history.filter((entry) => entry.key !== historyItem.key)].slice(0, 500),
    }));
    if (session) void api.saveHistory(historyItem).catch(() => undefined);
    window.open(historyItem.url, "_blank", "noopener,noreferrer");
  }

  function toggleFavorite(item: HotItem) {
    const existing = guestState.favorites.some((favorite) => favorite.key === item.fingerprint);
    if (existing) {
      setGuestState((current) => ({ ...current, favorites: current.favorites.filter((favorite) => favorite.key !== item.fingerprint) }));
      if (session) void api.deleteFavorite(item.fingerprint).catch(() => undefined);
      return;
    }
    const favorite: FavoriteItem = {
      key: item.fingerprint,
      title: item.title,
      url: item.url,
      platformId: item.platformId,
      createdAt: new Date().toISOString(),
    };
    setGuestState((current) => ({ ...current, favorites: [favorite, ...current.favorites] }));
    if (session) void api.saveFavorite(favorite).catch(() => undefined);
  }

  function removeFavorite(key: string) {
    setGuestState((current) => ({ ...current, favorites: current.favorites.filter((favorite) => favorite.key !== key) }));
    if (session) void api.deleteFavorite(key).catch(() => undefined);
  }

  function clearHistory() {
    setGuestState((current) => ({ ...current, history: [] }));
    if (session) void api.clearHistory().catch(() => undefined);
  }

  async function signOut() {
    await api.signOut();
    setSession(null);
    setAccountOpen(false);
    setCloudNotice("已退出登录，本地收藏和历史仍然保留。");
  }

  async function deleteAccount() {
    if (!window.confirm("确认注销账户并删除全部云端收藏、历史和设置吗？")) return;
    await api.deleteAccount();
    setSession(null);
    setAccountOpen(false);
    setCloudNotice("账户和云端数据已删除，本地数据仍然保留。");
  }

  return (
    <div className="app-shell">
      <Sidebar
        guestState={guestState}
        onChangeGuestState={updateGuestState}
        onClose={() => setSidebarOpen(false)}
        onOpenLibrary={openLibrary}
        onSelectPlatform={setSelectedPlatform}
        open={sidebarOpen}
        selectedPlatform={selectedPlatform}
      />

      <div className="main-column">
        <Header
          authChecking={authChecking}
          category={category}
          onCategoryChange={setCategory}
          onOpenAccount={() => setAccountOpen(true)}
          onOpenAuth={() => setAuthOpen(true)}
          onOpenLibrary={openLibrary}
          onOpenMenu={() => setSidebarOpen(true)}
          onQueryChange={setQuery}
          onRefresh={() => void loadPublicData(true)}
          query={query}
          refreshing={refreshing}
          session={session}
          updatedAt={updatedAt}
        />

        <main className="content">
          {loadError ? <div className="connection-notice"><span className="status-dot is-warning" />{loadError}</div> : null}
          {cloudNotice ? <button className="connection-notice cloud-notice" onClick={() => setCloudNotice("")} type="button"><span className="status-dot" />{cloudNotice}</button> : null}
          <AggregateBoard items={aggregate} loading={loading} onOpen={openAggregate} />

          <section className="hotlists-section" aria-labelledby="hotlists-title">
            <header className="section-heading hotlists-heading">
              <div>
                <span className="section-index">02</span>
                <div>
                  <h2 id="hotlists-title">平台榜单</h2>
                  <p>{selectedPlatform ? "当前平台" : category === "all" ? "已订阅平台" : "当前分类"} · {visiblePlatforms.length} 个</p>
                </div>
              </div>
              {selectedPlatform ? <button className="text-link" onClick={() => setSelectedPlatform(null)} type="button">返回全部榜单</button> : null}
            </header>

            {visiblePlatforms.length ? (
              <div className={`hotlist-grid ${query !== deferredQuery ? "is-filtering" : ""}`}>
                {visiblePlatforms.map((platform) => (
                  <HotlistCard
                    favoriteKeys={favoriteKeys}
                    hotlist={filteredHotlistMap.get(platform.id)}
                    key={platform.id}
                    onOpenItem={recordOpen}
                    onToggleFavorite={toggleFavorite}
                    platform={platform}
                  />
                ))}
              </div>
            ) : (
              <div className="no-results">
                <strong>没有找到符合条件的榜单</strong>
                <p>可以清空搜索、切换分类，或在左侧“管理平台”中恢复订阅。</p>
                <button className="primary-button" onClick={() => { setQuery(""); setCategory("all"); setSelectedPlatform(null); }} type="button">查看全部榜单</button>
              </div>
            )}
          </section>

          <footer className="site-footer">
            <span>今日聚合</span>
            <p>聚合二十四个平台；登录后收藏、历史和平台设置会同步到云端。</p>
            <a href="mailto:admin@3914103.xyz">联系站长</a>
          </footer>
        </main>
      </div>

      <LibraryPanel
        favorites={guestState.favorites}
        history={guestState.history}
        onChangeTab={setLibraryTab}
        onClearHistory={clearHistory}
        onClose={() => setLibraryOpen(false)}
        onRemoveFavorite={removeFavorite}
        open={libraryOpen}
        tab={libraryTab}
      />
      <AuthModal onAuthenticated={refreshSession} onClose={() => setAuthOpen(false)} open={authOpen} />
      <AccountPanel
        onClose={() => setAccountOpen(false)}
        onDeleteAccount={() => void deleteAccount()}
        onOpenAdmin={() => { setAccountOpen(false); setAdminOpen(true); }}
        onSignOut={() => void signOut()}
        open={accountOpen}
        session={session}
      />
      <AdminPanel onClose={() => setAdminOpen(false)} open={adminOpen} />
    </div>
  );
}
